import { AfterViewChecked, Component, ElementRef, inject } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { AbstractComponent } from '../../abstract.component';
import { TooltipService } from '../../../services/tooltip.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { SolTooltipsState } from './sol-tooltips.state';

@Component({
  selector: 'sol-tooltips',
  imports: [TranslatePipe, NgTemplateOutlet],
  templateUrl: './sol-tooltips.component.html',
})
export class SolTooltipsComponent extends AbstractComponent<SolTooltipsState> implements AfterViewChecked {
  // Dependencies
  private readonly tooltipService = inject(TooltipService);
  private readonly elementRef = inject(ElementRef);

  // Computed
  protected readonly visible$ = this.compute(state => state.visible);
  protected readonly overlays$ = this.compute(state => state.overlays);

  // Internal
  private clampedIds = new Set<string>();

  constructor() {
    super(SolTooltipsState.of());

    this.tooltipService.watch(state => this.update(s => s.withTooltips(state.tooltips)));
  }

  public ngAfterViewChecked(): void {
    const overlays = this.state.overlays;
    for (const overlay of overlays) {
      // Only clamp once per show cycle
      if (this.clampedIds.has(overlay.id)) {
        continue;
      }
      const el = this.elementRef.nativeElement.querySelector(`[data-tooltip-id="${overlay.id}"]`);
      if (!el) {
        continue;
      }
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        continue;
      }
      this.clampedIds.add(overlay.id);
      this.update(state =>
        state.withViewportClamp(
          overlay.id,
          rect,
          document.documentElement.clientWidth,
          document.documentElement.clientHeight,
        ),
      );
    }

    // Clean up clamped IDs for removed tooltips
    const activeIds = new Set(overlays.map(o => o.id));
    for (const id of this.clampedIds) {
      if (!activeIds.has(id)) {
        this.clampedIds.delete(id);
      }
    }
  }
}
